import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { formatDistanceToNow } from 'date-fns';

export interface CampaignCommentItem {
  id: string;
  name: string;
  type: 'support' | 'oppose';
  timeAgo: string;
  message: string;
  createdAt: string;
  isEdited: boolean;
  isOwnComment: boolean;
}

interface UseCampaignCommentsArgs {
  campaignId?: string;
  userId?: string;
}

export function useCampaignComments({ campaignId, userId }: UseCampaignCommentsArgs) {
  const [comments, setComments] = useState<CampaignCommentItem[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [canComment, setCanComment] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!campaignId) {
      setComments([]);
      return;
    }

    setLoadingComments(true);

    const { data, error } = await supabase.rpc('get_campaign_comments_public', {
      p_campaign_id: campaignId,
    });

    if (error) {
      console.error('Error fetching campaign comments:', error);
      setComments([]);
      setLoadingComments(false);
      return;
    }

    const normalized: CampaignCommentItem[] = (data ?? []).map((row: any) => {
      const first = row.first_name || '';
      const lastInitial = row.last_initial ? `${row.last_initial}.` : '';
      const fullName = `${first} ${lastInitial}`.trim() || 'Anonymous';

      const commentType: 'support' | 'oppose' =
        row.intent_type === 'oppose' ? 'oppose' : 'support';

      return {
        id: row.id,
        name: fullName,
        type: commentType,
        timeAgo: formatDistanceToNow(new Date(row.created_at), { addSuffix: true }),
        message: row.body,
        createdAt: row.created_at,
        isEdited: Boolean(row.is_edited),
        isOwnComment: row.user_id === userId,
      };
    });

    setComments(normalized);
    setLoadingComments(false);
  }, [campaignId, userId]);

  const fetchEligibility = useCallback(async () => {
    if (!campaignId || !userId) {
      setCanComment(false);
      return;
    }

    const { data, error } = await supabase.rpc('user_can_comment_on_campaign', {
      p_user_id: userId,
      p_campaign_id: campaignId,
    });

    if (error) {
      console.error('Error checking campaign comment eligibility:', error);
      setCanComment(false);
      return;
    }

    setCanComment(Boolean(data));
  }, [campaignId, userId]);

  const addComment = useCallback(
    async (body: string, intentType: 'support' | 'oppose' = 'support') => {
      if (!campaignId || !userId) {
        throw new Error('Missing campaign or user context.');
      }

      const trimmed = body.trim();

      if (!trimmed) {
        throw new Error('Comment cannot be empty.');
      }

      if (trimmed.length > 750) {
        throw new Error('Comment must be 750 characters or fewer.');
      }

      setSubmittingComment(true);

      const { error } = await supabase.from('campaign_comments').insert([
        {
          campaign_id: campaignId,
          user_id: userId,
          intent_type: intentType,
          body: trimmed,
        },
      ]);

      setSubmittingComment(false);

      if (error) {
        throw error;
      }

      await fetchComments();
    },
    [campaignId, userId, fetchComments]
  );

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    fetchEligibility();
  }, [fetchEligibility]);

  return {
    comments,
    loadingComments,
    submittingComment,
    canComment,
    addComment,
    refreshComments: fetchComments,
  };
}